// EXACT reference-site content mirror for Apex Quality Control.
// Local image paths point to /public/assets/img (downloaded from the reference site).

export const site = {
  name: 'Apex Quality Control',
  short: 'AQC',
  tagline: 'Quality You Can Trust. Excellence We Deliver.',
  eyebrow: 'INDEPENDENT INSPECTION · TESTING · AUDIT',
  description:
    'Independent inspection, testing, and audit services that help businesses ensure compliance, reduce risk, and deliver the highest quality to their customers.',
  email: 'info@aqc.so',
  phone: '+252 616 937 878',
  phoneRaw: '+252616937878',
  address: {
    line1: 'Goley Tower',
    line2: 'Hodan District',
    city: 'Mogadishu',
    country: 'Somalia',
  },
  hours: 'We reply within one business day.',
  socials: { linkedin: '#', twitter: '#', facebook: '#' },
};

export const heroStats = [
  { label: 'On-site within 72h' },
  { label: 'Report within 24h' },
  { label: 'ISO-accredited network' },
  { label: 'Truly independent' },
];

export const commitments = [
  { icon: 'Scale', title: 'Independent & Impartial', body: 'No stake in factories or buyers.' },
  { icon: 'Globe2', title: 'Global Standards', body: 'ISO, ASTM, EN, IEC — applied in the field.' },
  { icon: 'Zap', title: 'Fast & Reliable', body: 'On-site in 72h. Reports in 24h.' },
  { icon: 'Users', title: 'Experienced Professionals', body: 'Trained inspectors, one dedicated coordinator.' },
];

export const services = [
  {
    slug: 'quality-inspection',
    title: 'Quality Inspection',
    tagline: 'Independent verification against your specifications and AQL sampling.',
    detailIntro:
      'Verify goods at any stage of production against your specifications and ISO 2859-1 AQL sampling limits. From early production to sealed container.',
    icon: 'ClipboardCheck',
    image: '/assets/img/svc-quality-inspection.jpg',
    bullets: [
      'Conformity to buyer specifications, samples and approved drawings',
      'Workmanship, dimensions, weight, function and packaging',
      'AQL 2.5 / 4.0 sampling per ISO 2859-1 (or client-defined plan)',
      'Sorting and rework verification of defective units',
      'On-going production monitoring at defined checkpoints',
    ],
    deliverables: [
      'Full photo-documented inspection report within 24 hours',
      'Measurement, weight and function test records',
      'Defect log with severity classification (Critical / Major / Minor)',
      'Clear ship / hold recommendation',
    ],
  },
  {
    slug: 'product-testing',
    title: 'Product Testing',
    tagline: 'Laboratory verification against international standards.',
    detailIntro:
      'Independent laboratory testing coordinated through ISO 17025-accredited partner labs — physical, mechanical, chemical and safety properties, with clear pass/fail interpretation.',
    icon: 'FlaskConical',
    image: '/assets/img/svc-product-testing.jpg',
    bullets: [
      'Physical, mechanical, chemical and safety properties',
      'Standards coverage — ISO, ASTM, AATCC, EN, IEC and buyer-specific',
      'Express testing for time-critical shipments',
      'Coordination with ISO 17025-accredited partner laboratories',
      'Interpretation and pass/fail recommendation',
    ],
    deliverables: [
      'Certified laboratory test report',
      'Standard-referenced pass/fail summary',
      'Retention samples and chain-of-custody records',
      'Advisory notes on failed parameters',
    ],
  },
  {
    slug: 'vendor-audit',
    title: 'Vendor Audit',
    tagline: 'Factory capability, genuineness, social accountability & finance.',
    detailIntro:
      'Verify a factory’s capability, genuineness, quality system and social accountability — before you place the order and while you continue to work with them.',
    icon: 'ShieldCheck',
    image: '/assets/img/svc-vendor-audit.jpg',
    bullets: [
      'Manufacturing capability, capacity and quality system (ISO 9001)',
      'Genuineness verification to avoid supplier scams',
      'Social accountability audit — fair treatment, working conditions (SA8000)',
      'Financial soundness and business licence checks',
      'Code-of-conduct and buyer-specific verifications',
    ],
    deliverables: [
      'Full audit report with scoring by category',
      'Photo evidence and document register',
      'Risk flags and improvement recommendations',
      'Approve / conditional / reject recommendation',
    ],
  },
  {
    slug: 'cargo-survey',
    title: 'Cargo Survey & Loading',
    tagline: 'Supervise loading from factory floor to sealed container.',
    detailIntro:
      'Independent supervision of container loading, dunnage, sealing and quantity reconciliation — with photo-documented evidence and seal-number capture.',
    icon: 'Ship',
    image: '/assets/img/svc-cargo-survey.jpg',
    bullets: [
      'Quantity, carton marking and packing integrity',
      'Container condition, cleanliness and suitability',
      'Loading pattern, dunnage and securing',
      'Sealing, seal-number capture and photo record',
      'Bulk commodity draft, ullage and sampling',
    ],
    deliverables: [
      'Container Loading Check (CLC) report within 24 hours',
      'Seal-number and photo evidence pack',
      'Quantity reconciliation vs. packing list',
      'Any non-conformities documented and flagged',
    ],
  },
];

export const serviceCatalog = [
  'Finished Goods Inspection',
  'Loading Inspection',
  'Inbound Cargo Inspection',
  'Early Production QC',
  'During Production QC',
  'Production Monitoring',
  'Factory Audit',
  'SA8000 Social Audit',
  'Buyer Verifications',
  'Code of Conduct Audit',
  'Quality Sorting',
  'Bespoke Programmes',
];

export const checkpoints = [
  { n: '01', code: 'PPI', title: 'Pre-Production', body: 'Verify raw materials, components and sample approval before mass production starts.' },
  { n: '02', code: 'DUPRO', title: 'During-Production', body: 'Catch process drift early with in-line checks at 20–80% completion.' },
  { n: '03', code: 'PSI', title: 'Pre-Shipment', body: 'AQL-based sampling of finished, packed goods before they leave the factory.' },
  { n: '04', code: 'CLC', title: 'Container Loading', body: 'Supervise loading, sealing and documentation of the final shipment.' },
];

export const industries = [
  {
    id: 'food',
    title: 'Food, Seafood & Agriculture',
    icon: 'Wheat',
    short: 'Hygiene, cold-chain and labelling assurance.',
    long:
      'From farm to fork — we verify hygiene, cold-chain integrity, bacteriological quality and correct labelling for food, seafood and agricultural commodities.',
    bullets: [
      'Hygiene and GMP conditions on-site',
      'Cold-chain integrity and temperature logs',
      'Bacteriological and residue testing',
      'Nutrition and allergen labelling',
    ],
    tags: ['HACCP', 'East African Standards', 'ISO 22000 (aligned)'],
    image: '/assets/img/ind-food.jpg',
  },
  {
    id: 'textiles',
    title: 'Textiles, Apparel & Footwear',
    icon: 'Shirt',
    short: 'Workmanship, measurement, fibre and safety.',
    long:
      'Workmanship, measurement, colour, fibre content and safety checks — from fabric roll to finished garment or footwear.',
    bullets: [
      'Point-of-measurement (POM) and grading',
      'Colour, shade and fastness verification',
      'Fibre content and weight per unit area',
      'Metal detection, sharp points and small parts',
    ],
    tags: ['ISO', 'ASTM', 'AATCC', 'EN'],
    image: '/assets/img/ind-textiles.jpg',
  },
  {
    id: 'construction',
    title: 'Hardware & Construction',
    icon: 'HardHat',
    short: 'Cement, steel, tiles, fittings — dimensional and strength.',
    long:
      'Dimensional accuracy, strength, and conformity of cement, steel, tiles, fittings and hardware. On-site QC available via our Construction Quality Control Checklist.',
    bullets: [
      'Dimensional and tolerance verification',
      'Compressive and tensile strength testing',
      'Batch and mill certificate verification',
      'On-site QC per project checkpoints',
    ],
    tags: ['ISO', 'ASTM', 'EN', 'ACI'],
    image: '/assets/img/ind-construction.jpg',
  },
  {
    id: 'medical',
    title: 'Medical & Healthcare',
    icon: 'HeartPulse',
    short: 'Packaging, labelling and conformity for medical devices.',
    long: 'Packaging, labelling and conformity checks consistent with the ISO 13485 medical device quality framework.',
    bullets: [
      'Primary and secondary packaging integrity',
      'Sterile barrier and labelling verification',
      'Lot traceability and expiry date checks',
      'Conformity to declared specifications',
    ],
    tags: ['ISO 13485 (aligned)', 'ISO 11607'],
    image: '/assets/img/ind-medical.jpg',
  },
  {
    id: 'livestock',
    title: 'Livestock & Veterinary',
    icon: 'PawPrint',
    short: 'Pre-export inspection and veterinary-hygiene checks.',
    long:
      'Pre-export inspection, condition surveys and veterinary-hygiene checks for livestock destined for regional and international trade.',
    bullets: [
      'Herd condition and identification',
      'Documentation and veterinary certification',
      'Feed, water and handling conditions',
      'Loading and transit condition survey',
    ],
    tags: ['OIE (aligned)', 'Buyer country import protocols'],
    image: '/assets/img/ind-livestock.jpg',
  },
  {
    id: 'electrical',
    title: 'Electrical & Electronics',
    icon: 'Cpu',
    short: 'Function, safety and EMC — CE, FCC, RoHS.',
    long:
      'Function and safety testing — Hi-Pot, power, connectivity — plus CE, FCC and RoHS compliance verification for electrical and electronic products.',
    bullets: [
      'Hi-Pot, insulation and ground continuity',
      'Power, current draw and connectivity',
      'EMC and radio-frequency compliance',
      'RoHS restricted substance verification',
    ],
    tags: ['CE', 'FCC', 'RoHS', 'IEC'],
    image: '/assets/img/ind-electrical.jpg',
  },
  {
    id: 'industrial',
    title: 'Industrial Equipment',
    icon: 'Factory',
    short: 'Piping, valves, fabricated steel & machinery.',
    long:
      'Piping, valves, fabricated steel and machinery — checked against drawings, welding standards and dimensional tolerances.',
    bullets: [
      'Drawing conformity and dimensional check',
      'Weld visual inspection and NDT arrangement',
      'Material and mill test certificate review',
      'Functional and pressure testing',
    ],
    tags: ['ASME', 'ASTM', 'ISO', 'EN'],
    image: '/assets/img/ind-industrial.jpg',
  },
  {
    id: 'commodities',
    title: 'Minerals, Oil, Gas & Commodities',
    icon: 'Fuel',
    short: 'Sampling, quantity, cargo survey and lab assay.',
    long:
      'Sampling, quantity determination, cargo survey and laboratory assay for minerals, oil, gas and bulk commodities.',
    bullets: [
      'Representative sampling and chain-of-custody',
      'Quantity determination — draft and ullage surveys',
      'Cargo condition and contamination check',
      'Laboratory assay coordination',
    ],
    tags: ['ISO', 'ASTM', 'API (aligned)'],
    image: '/assets/img/ind-commodities.jpg',
  },
];

export const standards = [
  { code: 'ISO 17020', name: 'Inspection bodies', desc: 'Requirements for the operation of bodies performing inspection.', applies: 'All inspection activities' },
  { code: 'ISO 17025', name: 'Accredited testing laboratories', desc: 'General requirements for the competence of testing and calibration labs.', applies: 'Product testing (partner labs)' },
  { code: 'ISO 2859-1', name: 'AQL sampling', desc: 'Sampling procedures for inspection by attributes.', applies: 'All product inspections' },
  { code: 'ISO 9001', name: 'Quality management systems', desc: 'Framework for organisational quality management.', applies: 'Vendor audits' },
  { code: 'ISO 13485', name: 'Medical devices — QMS', desc: 'QMS requirements for medical device manufacture.', applies: 'Medical & healthcare' },
  { code: 'ASTM F963', name: 'Toy safety (US)', desc: 'US consumer safety standard for toy safety.', applies: "Toys, children's products" },
  { code: 'EN 71', name: 'Toy safety (EU)', desc: 'European standard for the safety of toys.', applies: "Toys, children's products" },
  { code: 'CE / FCC / RoHS', name: 'Electrical compliance', desc: 'Safety, EMC and restricted substances for electronic products.', applies: 'Electrical & electronics' },
  { code: 'HACCP', name: 'Food safety', desc: 'Hazard analysis and critical control points.', applies: 'Food, seafood, agriculture' },
  { code: 'SA8000', name: 'Social accountability', desc: 'International standard for decent workplace conditions.', applies: 'Vendor / social audits' },
  { code: 'EAS', name: 'East African Standards', desc: 'Regional standards issued by the East African Community.', applies: 'Regional trade — food, materials, commodities' },
];

export const values = [
  { title: 'Independence', body: 'We hold no stake in factories or buyers — our verdict is unbiased.' },
  { title: 'Professionalism', body: 'Every inspection is delivered by trained, experienced quality professionals.' },
  { title: 'Reliability', body: 'On-site within 72 hours, reports within 24 hours, every time.' },
  { title: 'Global Standards', body: 'Aligned with ISO, ASTM, EN, ACI and industry-specific benchmarks.' },
];

export const approach = [
  { title: 'Compliance', body: 'We check that products, processes and shipments comply with the standards and buyer requirements that matter.' },
  { title: 'Quality Workmanship', body: 'We verify that goods are built and finished to specification, with no shortcuts on the shop floor.' },
  { title: 'Correct Use of Materials', body: 'We confirm that the right materials — at the right grade — are actually used, not just declared on paper.' },
  { title: 'Proactive Independent Eye', body: 'We catch issues early, on the ground, before they become costly problems at your destination.' },
];

export const countries = [
  { flag: '🇸🇴', name: 'Somalia', badge: 'HQ' },
  { flag: '🇮🇳', name: 'India' },
  { flag: '🇨🇳', name: 'China' },
  { flag: '🇹🇷', name: 'Turkey' },
  { flag: '🇬🇧', name: 'United Kingdom' },
  { flag: '🇭🇺', name: 'Hungary' },
  { flag: '🇳🇮', name: 'Nicaragua' },
  { flag: '🇹🇭', name: 'Thailand' },
  { flag: '🇵🇭', name: 'Philippines' },
  { flag: '🇲🇿', name: 'Mozambique' },
];

export const whyAqc = [
  { title: 'Truly Independent.', body: 'We have no financial ties to factories or buyers. Our only product is an honest, evidence-based verdict.' },
  { title: 'On-site within 72 hours.', body: 'Our regional coordinators dispatch inspectors quickly — critical when production windows are tight.' },
  { title: 'Reports within 24 hours.', body: 'Full photo-documented reports delivered within a business day of the inspection completing.' },
  { title: 'One Coordinator, Online.', body: 'You get a single point of contact who owns your programme end-to-end, reachable online.' },
  { title: 'Rooted in Somalia, Working Globally.', body: 'Headquartered in Mogadishu with a coordinated inspection network across 10 countries.' },
  { title: 'Aligned to International Standards.', body: 'ISO, ASTM, EN, IEC and industry-specific standards — applied consistently in the field.' },
];

export const resources = [
  { title: 'Company Profile', desc: 'Overview of AQC, our services, industries and global network.', meta: 'PDF · 474 KB', href: '#' },
  { title: 'Construction Quality Control Checklist', desc: 'Concise on-site QC reference aligned to international building standards and the AQC inspection methodology.', meta: 'PDF · 4 KB', href: '#' },
];

export const galleryImages = [
  { category: 'Inspections', src: '/assets/img/svc-quality-inspection.jpg', alt: 'Inspector reviewing goods on the factory floor' },
  { category: 'Lab', src: '/assets/img/svc-product-testing.jpg', alt: 'Product testing in an accredited laboratory' },
  { category: 'Team', src: '/assets/img/svc-vendor-audit.jpg', alt: 'Vendor audit walkthrough of a manufacturing facility' },
  { category: 'Cargo', src: '/assets/img/svc-cargo-survey.jpg', alt: 'Container loading supervision at the port' },
  { category: 'Inspections', src: '/assets/img/ind-food.jpg', alt: 'Food and agriculture QC inspection' },
  { category: 'Inspections', src: '/assets/img/ind-textiles.jpg', alt: 'Textiles quality inspection on the production line' },
  { category: 'Inspections', src: '/assets/img/ind-construction.jpg', alt: 'On-site construction quality control' },
  { category: 'Lab', src: '/assets/img/ind-electrical.jpg', alt: 'Electrical safety and function testing' },
  { category: 'Inspections', src: '/assets/img/ind-industrial.jpg', alt: 'Industrial equipment dimensional check' },
  { category: 'Team', src: '/assets/img/about-team.jpg', alt: 'The AQC coordination team' },
];

export const heroImage = '/assets/img/hero-inspector.jpg';
export const aboutImage = '/assets/img/about-team.jpg';
export const ctaImage = '/assets/img/hero-port.jpg';

// ---------------------------------------------------------------------------
// Extended editorial content — About, Services, Service detail, Industries.
// General professional content that does not invent facts, certifications,
// clients, statistics or history.
// ---------------------------------------------------------------------------

export const aboutContent = {
  whoWeAre: {
    title: 'Who We Are',
    body:
      'Apex Quality Control is an independent inspection, testing and audit provider. Our work supports importers, exporters, manufacturers, traders and buyers who need an impartial view of the goods, factories and shipments they rely on. We operate as a third party — with no commercial stake in the products we assess — and every conclusion we deliver is grounded in on-site observation, agreed specifications and internationally recognised inspection practice.',
  },
  purpose: {
    title: 'Our Purpose',
    body:
      'Our purpose is to help clients make informed, evidence-based decisions about quality before goods leave a factory or arrive at their destination. By checking products, processes and shipments against agreed requirements, we help reduce the risk of quality-related surprises, rework, disputes and customer dissatisfaction further down the supply chain.',
  },
  mission: {
    title: 'Our Mission',
    body:
      'To provide clear, timely and honest quality-control services that give our clients a reliable basis for accepting, rejecting or negotiating a shipment — supported by structured reports, photographs, measurements and standards-referenced observations.',
  },
  vision: {
    title: 'Our Vision',
    body:
      'To be a trusted independent partner for organisations that treat quality as a business decision — from the factory floor to the final destination — while continuing to strengthen quality practices in the markets we serve.',
  },
  approachToQc: {
    title: 'Our Approach to Quality Control',
    body:
      'We build every assignment around four things: the client’s specification, the applicable standard, the observable condition of the goods, and the evidence that supports our findings. Inspections are planned in advance, executed against a defined checklist, and reported in a format the client can share internally, with suppliers, insurers or downstream customers.',
    bullets: [
      'Assignment scope agreed in writing before the visit.',
      'Inspection performed against the client’s specification and applicable standards.',
      'Sampling plans applied consistently — typically per ISO 2859-1.',
      'Findings recorded with photographs, measurements and defect classification.',
      'Report delivered promptly, with a clear recommendation.',
    ],
  },
  independence: {
    title: 'Our Independence and Impartiality',
    body:
      'Independence is the foundation of useful quality-control work. Apex Quality Control does not manufacture, buy or sell the goods we inspect. Our inspectors record what they observe against the agreed requirements — nothing is softened, exaggerated or filtered to favour any party. Where a finding is not conclusive, we say so; where a defect is critical, we say so as well.',
  },
  technical: {
    title: 'Our Technical Approach',
    body:
      'Our inspectors work to written procedures aligned with ISO 17020 practice for the operation of inspection bodies, and coordinate laboratory work with ISO 17025-accredited partner laboratories where testing is required. Sampling is based on ISO 2859-1 attributes sampling or a client-defined plan. Product-specific checks reference the standards that apply to the product family — such as ASTM, EN, IEC, HACCP-aligned or industry-specific benchmarks.',
  },
  coverage: {
    title: 'Our Geographic Coverage',
    body:
      'Headquartered in Mogadishu, Somalia, Apex Quality Control coordinates work through a network of inspectors and partner offices in several manufacturing regions. Where we do not have a permanent presence, we mobilise trained inspectors through vetted partners so clients receive a consistent standard of work, regardless of where the goods are produced or loaded.',
  },
  commitmentClients: {
    title: 'Our Commitment to Clients',
    body:
      'We treat every assignment — small or large — as a professional engagement. Clients receive a single coordinator responsible for their programme, a documented scope of work, and a report format that supports downstream decisions. Where issues are found, we describe the finding, the affected quantity, and the practical options available to the client.',
    bullets: [
      'One coordinator responsible for your programme.',
      'A written scope before the inspector is dispatched.',
      'Reports delivered promptly after the inspection completes.',
      'Practical, evidence-based recommendations — never absolute guarantees.',
    ],
  },
  commitmentAccuracy: {
    title: 'Our Commitment to Accuracy',
    body:
      'Accuracy in reporting is not optional. Findings are recorded as observations against agreed requirements — with the quantities inspected, the sample size, the measurement method and photographic evidence. Where information cannot be verified at the time of the visit, we state that clearly, so the client can decide how to proceed.',
  },
  ethics: {
    title: 'Our Professional Ethics',
    body:
      'Every inspector is expected to work with objectivity, honesty and confidentiality. We do not accept payments or favours from the parties we inspect. Client information — specifications, drawings, supplier details, pricing — is treated as confidential and used only for the purpose of the engagement.',
    bullets: [
      'Objectivity — findings driven by observation, not preference.',
      'Confidentiality — client information handled with care.',
      'Anti-corruption — no gifts, favours or payments from inspected parties.',
      'Traceability — every finding tied to evidence.',
    ],
  },
  whyQcMatters: {
    title: 'Why Quality Control Matters',
    body:
      'Quality problems that reach the destination are far more expensive than those caught early. An independent inspection at the right checkpoint helps identify defects, mislabelling, missing items and packing problems before goods are sealed into a container. For many products, the cost of a single professional inspection is a small fraction of the value it protects.',
  },
  supportImporters: {
    title: 'How We Support Importers and Exporters',
    body:
      'Importers use our reports to verify that goods conform to the agreed specification before releasing payment or accepting the shipment. Exporters use the same reports to demonstrate that the goods leaving the factory meet the buyer’s expectations. In both cases, the independent nature of the report reduces the likelihood of disputes and supports faster commercial resolution.',
  },
  supportManufacturers: {
    title: 'How We Support Manufacturers and Suppliers',
    body:
      'For factories and suppliers, an independent inspection provides an objective checkpoint before goods are shipped. Findings can be used to correct workmanship, packaging or labelling issues internally — often before the goods reach the buyer. Over time, this can support improvements in the supplier’s own quality-management practices.',
  },
};

export const servicesPageContent = {
  intro: {
    title: 'A Complete View of Product and Shipment Quality',
    body:
      'Apex Quality Control offers a structured set of inspection, testing, audit and cargo-supervision services designed to give clients evidence-based visibility of the goods they buy or ship. Each service is delivered by trained inspectors, follows a documented checklist, and results in a written report — with photographs — that clients can use to make commercial and quality decisions.',
  },
  whyIndependent: {
    title: 'Why Independent Inspection Matters',
    body:
      'When quality is verified only by the supplier or only by the buyer, it becomes difficult to resolve disputes objectively. An independent third-party inspection provides a neutral view of the goods at the checkpoint that matters most. Independent reports help both parties agree on what was actually present in the shipment — reducing risk, delays and disagreement.',
    bullets: [
      'A neutral view of the goods, evaluated against the agreed specification.',
      'Findings that can be used by buyer and seller to resolve issues objectively.',
      'Early identification of defects, mislabelling and packing errors.',
      'A documented record of the goods’ condition at the inspection point.',
    ],
  },
  process: {
    title: 'How the Inspection Process Works',
    steps: [
      { title: 'Request', body: 'The client shares the product specification, the factory location, the target inspection window and any special requirements.' },
      { title: 'Planning', body: 'A coordinator confirms the scope, checklist and standards, and mobilises the assigned inspector.' },
      { title: 'On-site work', body: 'The inspector performs the checks: quantity, workmanship, dimensions, function and packing — with photographs and measurements recorded on site.' },
      { title: 'Reporting', body: 'A structured inspection report is delivered promptly, with defect classification and a clear recommendation.' },
    ],
  },
  documentation: {
    title: 'Typical Inspection Documentation',
    bullets: [
      'Client-supplied specification, drawings or approved sample references.',
      'Inspection checklist and sampling plan.',
      'Measurement and function test records.',
      'Photographs of the goods, packaging and any defects.',
      'A structured PDF report with severity-classified defects and a recommendation.',
    ],
  },
  benefits: {
    title: 'Benefits of Professional Quality Control',
    bullets: [
      'Helps reduce the risk of defective or non-conforming goods reaching the destination.',
      'Supports informed decisions about accepting, rejecting or reworking a shipment.',
      'Provides an objective basis for supplier discussions and commercial resolution.',
      'Documents the condition of the goods at a defined checkpoint.',
      'Supports internal quality improvement in the client’s supply chain.',
    ],
  },
  howToRequest: {
    title: 'How to Request a Service',
    body:
      'Send us a short brief covering the product, quantity, factory location and target inspection date. A coordinator will confirm scope, price and inspector availability, and share a written booking summary before the visit. For repeat programmes, a standing arrangement can be set up so future bookings take only a few minutes.',
  },
};

// Extended per-service content used by ServiceDetail.
export const serviceExtra = {
  'quality-inspection': {
    purpose:
      'Quality inspection provides an independent, evidence-based view of whether the goods conform to the agreed specification. Depending on the stage of production, it can catch material issues, process drift, defective workmanship or packaging errors before goods leave the factory.',
    whenRequired: [
      'Before production starts — to verify raw materials and approved samples.',
      'Once 20–80% of the goods are produced — to catch process drift early.',
      'When goods are finished and packed — before they leave the factory.',
      'When quantity, workmanship or packing needs to be verified.',
    ],
    whoBenefits: [
      'Importers verifying goods before releasing final payment.',
      'Exporters demonstrating conformity before shipment.',
      'Traders acting between suppliers and buyers.',
      'Brands protecting their reputation at the point of dispatch.',
    ],
    process: [
      { title: 'Initial Production Inspection', body: 'Verify raw materials, components and approved samples before mass production begins.' },
      { title: 'During-Production Inspection', body: 'Check goods on the line at 20–80% completion to catch process drift early.' },
      { title: 'Pre-Shipment Inspection', body: 'AQL-based sampling of finished, packed goods before they leave the factory.' },
      { title: 'Final Random Inspection', body: 'A statistically representative sample from the finished lot, inspected in random order.' },
      { title: 'Workmanship Review', body: 'Check for surface, structural and finishing defects against approved samples and the client specification.' },
      { title: 'Product Specification Checks', body: 'Verify colour, size, materials, markings, features and function against the specification.' },
      { title: 'Packaging & Labelling Checks', body: 'Verify carton marking, inner and outer packaging, barcodes, batch codes and country-of-origin labelling.' },
    ],
    clientDocs: [
      'Product specification, drawings or approved sample references.',
      'Purchase order or packing list with expected quantity.',
      'Any client-specific defect classification or AQL plan.',
    ],
    reporting:
      'Reports are structured with a summary, defect log (Critical / Major / Minor), photographic evidence, measurement records and a clear ship/hold recommendation. Delivered promptly after the inspection completes.',
    benefits: [
      'Helps identify defects before goods are sealed into a container.',
      'Supports commercial decisions about accepting or rejecting a shipment.',
      'Provides an evidence-based record of the goods at the inspection point.',
    ],
    limitations: [
      'Findings reflect the goods sampled and observed at the time of the inspection.',
      'An inspection does not guarantee the condition of un-sampled units.',
      'Latent defects that cannot be detected visually may require laboratory testing.',
    ],
    faq: [
      { q: 'Is an inspection the same as laboratory testing?', a: 'No. An inspection is a visual, dimensional and functional check performed on site. Laboratory testing verifies material or performance properties that cannot be checked on the shop floor.' },
      { q: 'How much notice do you need?', a: 'We aim to dispatch inspectors within 72 hours where possible. For complex products or remote locations, more notice supports better planning.' },
      { q: 'What sampling plan do you use?', a: 'By default, ISO 2859-1 (typically AQL 2.5 / 4.0). Client-defined plans are also supported.' },
    ],
    related: ['product-testing', 'cargo-survey', 'vendor-audit'],
  },
  'product-testing': {
    purpose:
      'Product testing provides laboratory-based verification of material, mechanical, chemical or safety properties that cannot be observed visually on the shop floor. Testing is coordinated with partner laboratories and evaluated against the agreed specification or the applicable standard.',
    whenRequired: [
      'When conformity depends on properties that cannot be inspected visually.',
      'When a buyer, retailer or regulator requires a certified laboratory report.',
      'When a new product or component needs to be qualified before mass production.',
      'When failure analysis is required after a quality incident.',
    ],
    whoBenefits: [
      'Importers introducing a new product into a regulated market.',
      'Manufacturers validating a new material or component.',
      'Brands and retailers requiring documented conformity for their range.',
    ],
    process: [
      { title: 'Purpose of Product Testing', body: 'Confirm that a product’s properties conform to the agreed specification or the applicable standard.' },
      { title: 'Testing Against Agreed Specifications', body: 'Every test is executed against a written specification or a named standard — never a subjective preference.' },
      { title: 'Sample Identification', body: 'Samples are identified, labelled and photographed. Chain-of-custody is maintained through to the laboratory.' },
      { title: 'Testing Coordination', body: 'Testing is performed by an ISO 17025-accredited partner laboratory selected for the scope of work.' },
      { title: 'Applicable Standards', body: 'Common references include ISO, ASTM, AATCC, EN and IEC — plus industry-specific standards where applicable.' },
      { title: 'Review of Test Results', body: 'Laboratory results are reviewed and interpreted in the context of the client’s specification.' },
      { title: 'Documentation & Reporting', body: 'A structured report with the raw laboratory certificate and a plain-language pass/fail summary is delivered to the client.' },
    ],
    clientDocs: [
      'Specification or standard against which the product will be tested.',
      'Description of the sample and its intended use.',
      'Any prior test reports, if the product has been tested before.',
    ],
    reporting:
      'Clients receive the laboratory-issued test report plus a plain-language summary that references the applicable standard, the test result and any advisory notes on failed parameters.',
    benefits: [
      'Provides certified evidence of conformity against the applicable standard.',
      'Supports market-entry, retailer and regulatory documentation requirements.',
      'Helps identify failure modes that visual inspection cannot detect.',
    ],
    limitations: [
      'Test results apply to the sample tested and to the conditions of the test.',
      'Apex Quality Control does not operate its own laboratories; testing is coordinated with accredited partners.',
      'Testing is one part of a conformity picture — physical inspection often complements it.',
    ],
    faq: [
      { q: 'Do you operate your own laboratories?', a: 'No. Testing is coordinated with ISO 17025-accredited partner laboratories selected for the scope of work.' },
      { q: 'How long does testing take?', a: 'It depends on the test. Express testing is available for time-critical shipments where the laboratory scope allows it.' },
      { q: 'Can testing replace a physical inspection?', a: 'Rarely. Testing complements inspection; a well-designed programme usually includes both.' },
    ],
    related: ['quality-inspection', 'vendor-audit'],
  },
  'vendor-audit': {
    purpose:
      'A vendor audit is an on-site assessment of a factory or supplier against defined criteria — typically manufacturing capability, quality-management arrangements, working conditions and business identity. It supports the decision to approve, conditionally approve or reject a supplier.',
    whenRequired: [
      'Before placing an order with a new supplier.',
      'When a supplier’s recent quality performance raises concern.',
      'When a buyer, brand or retailer requires periodic supplier verification.',
      'When social-accountability arrangements need to be reviewed.',
    ],
    whoBenefits: [
      'Buyers evaluating a new supplier before their first order.',
      'Brands and retailers maintaining an approved-supplier list.',
      'Importers responding to a specific supplier-risk concern.',
    ],
    process: [
      { title: 'Supplier Identity & Business-Information Review', body: 'Confirm the supplier’s registered identity, business licence and basic corporate details available on site.' },
      { title: 'Production Capability', body: 'Observe production lines, output capacity and the range of products actually manufactured on the premises.' },
      { title: 'Quality-Management Arrangements', body: 'Review the supplier’s internal QC procedures, checklists and record-keeping — aligned with ISO 9001 principles.' },
      { title: 'Machinery & Equipment', body: 'Observe the condition, calibration status (where visible) and suitability of equipment used for the client’s products.' },
      { title: 'Workforce & Production Organization', body: 'Observe workforce arrangements, working conditions, shift patterns and general housekeeping.' },
      { title: 'Raw-Material Handling', body: 'Observe how raw materials are received, stored, identified and moved to the production line.' },
      { title: 'Production Processes', body: 'Observe key process steps, in-line controls and how non-conforming product is handled.' },
      { title: 'Internal Inspection Procedures', body: 'Review the supplier’s internal inspection records, defect classification and rework procedures.' },
      { title: 'Storage & Warehouse Conditions', body: 'Assess the conditions under which finished goods and raw materials are stored.' },
      { title: 'Documentation Review', body: 'Review certificates, mill certificates and product records that are made available on site.' },
      { title: 'Corrective-Action Observations', body: 'Where issues are observed, describe them and note any corrective actions the supplier has committed to.' },
    ],
    clientDocs: [
      'Supplier name, address and contact details.',
      'The product range or category to be audited.',
      'Any buyer-specific code of conduct or checklist to apply.',
    ],
    reporting:
      'Audit reports are structured by category, with observed strengths, weaknesses and photographic evidence. A clear recommendation is provided: approve, conditionally approve or reject, along with the reasoning behind it.',
    benefits: [
      'Provides an objective view of a supplier’s capability before committing to an order.',
      'Helps identify risks that are not visible from documents or online research.',
      'Supports responsible supplier selection and long-term supply-chain quality.',
    ],
    limitations: [
      'A vendor audit is an on-site observation at a point in time.',
      'It is not a legal guarantee of the supplier’s ongoing behaviour or performance.',
      'It is not a complete financial verification — audited financial statements are outside its scope.',
    ],
    faq: [
      { q: 'How long does an audit take?', a: 'Most factory audits are completed in a single business day, with the scope agreed in advance.' },
      { q: 'Do you audit against a client’s own code of conduct?', a: 'Yes. Buyer-specific codes of conduct and checklists can be applied when they are provided in advance.' },
      { q: 'Is a vendor audit the same as a certification audit?', a: 'No. A vendor audit is a supplier-verification exercise, not a certification against a specific management-system standard.' },
    ],
    related: ['quality-inspection', 'product-testing'],
  },
  'cargo-survey': {
    purpose:
      'Cargo survey and loading supervision provides an independent record of how goods are packed, loaded and sealed into a container or vessel. It supports commercial and insurance conversations later, if any question arises about the condition or quantity at the point of loading.',
    whenRequired: [
      'When goods are being loaded into a container for export or import.',
      'When a buyer or insurer requires an independent loading record.',
      'When bulk commodities need draft, ullage or sampling verification.',
      'When cargo condition and container suitability need to be documented.',
    ],
    whoBenefits: [
      'Importers taking responsibility for the goods at the point of loading.',
      'Exporters demonstrating that the container left the factory in good order.',
      'Insurers requiring documented loading conditions.',
    ],
    process: [
      { title: 'Cargo Condition Observations', body: 'Review the outward condition of the cartons, pallets or drums presented for loading.' },
      { title: 'Quantity & Package Verification', body: 'Reconcile the number of packages against the packing list and count against a defined sample.' },
      { title: 'Container Condition Review', body: 'Check the container for cleanliness, dryness, structural integrity and suitability for the cargo type.' },
      { title: 'Loading Supervision', body: 'Observe the loading pattern, dunnage, blocking and securing arrangements.' },
      { title: 'Cargo Arrangement Observations', body: 'Record how the cargo is arranged inside the container to identify any risk of shifting or damage.' },
      { title: 'Packaging & Marking Review', body: 'Verify that packaging, marking and handling symbols match the packing list and buyer requirements.' },
      { title: 'Seal-Number Recording', body: 'Record and photograph the container seal number once the doors are closed.' },
      { title: 'Photographic Documentation', body: 'Take structured photographs of the cargo, container, loading process and final seal.' },
      { title: 'Loading Progress Records', body: 'Keep a chronological record of the loading process for reference in later discussions.' },
      { title: 'Final Loading Report', body: 'Deliver a structured Container Loading Check (CLC) report with photographs and observations.' },
    ],
    clientDocs: [
      'Packing list and commercial invoice.',
      'Container number and expected seal number.',
      'Any special stowage or handling instructions.',
    ],
    reporting:
      'Reports include the packing list reconciliation, container condition, loading photographs, seal-number capture and any non-conformities observed during loading — delivered promptly after loading completes.',
    benefits: [
      'Documents the condition of the goods at the point of loading.',
      'Provides evidence for any later commercial or insurance discussion.',
      'Helps reduce disputes about quantity, packaging and container condition.',
    ],
    limitations: [
      'Findings reflect the loading operation observed on the day.',
      'Cargo survey does not replace laboratory testing or a full product inspection.',
      'Damage that occurs after the container is sealed is outside the scope of the loading survey.',
    ],
    faq: [
      { q: 'Do you supervise loading for bulk commodities?', a: 'Yes — including draft, ullage and sampling arrangements where relevant.' },
      { q: 'Can loading supervision replace a pre-shipment inspection?', a: 'No. Loading supervision is complementary; a PSI verifies quality, while a CLC verifies the loading operation.' },
      { q: 'Do you capture the seal number?', a: 'Yes. The container seal number is recorded and photographed after doors are closed.' },
    ],
    related: ['quality-inspection', 'product-testing'],
  },
};

// Extended per-industry content used by Industries page.
export const industryExtra = {
  food: {
    intro:
      'Food, seafood and agricultural commodities carry hygiene, cold-chain and labelling risks that can quickly translate into consumer or regulatory issues. Independent checks help confirm the condition, handling and labelling of goods before they enter distribution.',
    risks: ['Cold-chain breaks during handling or transit', 'Hygiene and cross-contamination on site', 'Incorrect nutrition, allergen or origin labelling', 'Damaged or leaking packaging'],
    items: ['Packaged and canned food', 'Frozen and chilled seafood', 'Agricultural commodities in bulk or bags', 'Beverages'],
    checkpoints: ['On-site hygiene and GMP observation', 'Temperature and cold-chain records review', 'Packaging integrity and seal check', 'Labelling and country-of-origin verification'],
    packaging: ['Primary packaging integrity', 'Batch and expiry-date verification', 'Cold-chain-friendly outer packaging', 'Country-of-origin and allergen labelling'],
    documentation: ['Purchase order and packing list', 'Product specification and labels', 'Cold-chain temperature logs (where applicable)'],
    relatedServices: ['quality-inspection', 'product-testing', 'cargo-survey'],
  },
  textiles: {
    intro:
      'Textiles, apparel and footwear are especially sensitive to workmanship, colour and measurement variation. Small deviations from the approved sample can lead to buyer rejections and costly rework — often after the goods have already been shipped.',
    risks: ['Workmanship defects on visible seams and finishing', 'Colour and shade variation from the approved sample', 'Measurement drift outside the tolerance', 'Metal contamination or sharp points in children’s items'],
    items: ['Woven and knit apparel', 'Home textiles', 'Footwear and accessories', 'Fabric rolls'],
    checkpoints: ['Point-of-measurement (POM) and grading', 'Colour and shade against the approved swatch', 'Fibre content and weight per unit area', 'Metal detection and small-parts checks'],
    packaging: ['Hangtags and care labels', 'Polybag safety warnings', 'Carton dimensions and stack marking'],
    documentation: ['Approved sample or size-set reference', 'Tech pack and measurement chart', 'Colour swatch or Pantone reference'],
    relatedServices: ['quality-inspection', 'product-testing', 'vendor-audit'],
  },
  construction: {
    intro:
      'Cement, steel, tiles, fittings and construction hardware are usually acceptance-tested against dimensional, weight and strength requirements. Site-level defects can be difficult to correct once the goods are on the project.',
    risks: ['Dimensional deviations outside tolerance', 'Substituted materials or incorrect grades', 'Damaged or non-conforming finishes', 'Missing batch or mill certificates'],
    items: ['Cement and admixtures', 'Structural and reinforcement steel', 'Tiles, sanitary ware and fittings', 'Fasteners and general hardware'],
    checkpoints: ['Dimensional and tolerance verification', 'Compressive and tensile strength testing (through partner labs)', 'Batch and mill certificate verification', 'On-site QC per project checkpoints'],
    packaging: ['Pallet integrity and shrink-wrap condition', 'Bag condition and moisture protection for cement', 'Batch marking and lot traceability'],
    documentation: ['Project or purchase specification', 'Mill or batch certificates', 'Site QC checklist (where applicable)'],
    relatedServices: ['quality-inspection', 'product-testing', 'cargo-survey'],
  },
  medical: {
    intro:
      'Medical and healthcare goods require particularly disciplined checks on packaging, labelling, lot traceability and sterile-barrier integrity. Findings need to be recorded in a way that supports downstream compliance work.',
    risks: ['Compromised primary or secondary packaging', 'Missing lot number, expiry date or intended-use markings', 'Sterile-barrier damage in transit', 'Non-conforming labelling in the destination language'],
    items: ['Disposable medical devices', 'Diagnostic consumables', 'Healthcare packaging', 'Non-sterile healthcare accessories'],
    checkpoints: ['Primary and secondary packaging integrity', 'Sterile barrier and labelling verification', 'Lot traceability and expiry date checks', 'Conformity to declared specifications'],
    packaging: ['Tamper-evident closures', 'Language-specific labelling for the destination', 'Batch and expiry markings'],
    documentation: ['Product specification and labelling brief', 'Batch record and expiry structure', 'Any device master file supplied for reference'],
    relatedServices: ['quality-inspection', 'product-testing'],
  },
  livestock: {
    intro:
      'Livestock and veterinary shipments require condition, handling and documentation checks — often against destination-country import protocols and veterinary requirements.',
    risks: ['Poor condition of animals at loading', 'Missing or inconsistent veterinary documentation', 'Inadequate handling and welfare arrangements', 'Non-conforming feed, water or shelter arrangements'],
    items: ['Live cattle, sheep and goats', 'Veterinary supplies', 'Feed for livestock in transit'],
    checkpoints: ['Herd condition and identification', 'Documentation and veterinary certification', 'Feed, water and handling conditions', 'Loading and transit condition survey'],
    packaging: ['Transport pen condition', 'Feed and water arrangements', 'Ventilation and shading'],
    documentation: ['Veterinary certificates for the shipment', 'Buyer country import protocol', 'Identification records for the herd'],
    relatedServices: ['quality-inspection', 'cargo-survey'],
  },
  electrical: {
    intro:
      'Electrical and electronic products need functional, safety and compliance checks. A well-designed programme combines a physical inspection with targeted testing at partner laboratories.',
    risks: ['Non-conformance to safety, EMC or restricted-substance requirements', 'Hidden functional defects that only appear under load', 'Missing markings, warnings or user documentation', 'Undeclared design changes vs the approved sample'],
    items: ['Consumer electronics', 'Small appliances', 'Cables, accessories and adaptors', 'LED lighting and drivers'],
    checkpoints: ['Hi-Pot, insulation and ground continuity', 'Power, current draw and connectivity', 'EMC and radio-frequency compliance', 'RoHS restricted-substance verification'],
    packaging: ['User-manual and warning-label inclusion', 'Accessory checklist and bundle content', 'Anti-static and impact protection'],
    documentation: ['Product specification and rating plate', 'CE, FCC or RoHS documentation, where declared', 'Approved sample or golden-sample reference'],
    relatedServices: ['quality-inspection', 'product-testing', 'vendor-audit'],
  },
  industrial: {
    intro:
      'Industrial equipment — piping, valves, fabricated steel, machinery — is typically inspected against drawings, welding standards and dimensional tolerances. Findings should be recorded with measurements and photographs at the same detail level as the drawings themselves.',
    risks: ['Dimensional deviation from the drawing', 'Weld and finish defects', 'Substituted materials or missing mill certificates', 'Functional or pressure test failure'],
    items: ['Piping and valves', 'Fabricated steel structures', 'Industrial machinery and sub-assemblies', 'Pressure vessels (buyer-defined scope)'],
    checkpoints: ['Drawing conformity and dimensional check', 'Weld visual inspection and NDT arrangement', 'Material and mill test certificate review', 'Functional and pressure testing'],
    packaging: ['Corrosion-protection wrapping', 'Skidding, crating and lifting-point marking', 'Serial-number and drawing-reference marking'],
    documentation: ['Approved drawings and welding procedure specifications', 'Mill and material certificates', 'Any buyer-defined inspection test plan'],
    relatedServices: ['quality-inspection', 'product-testing', 'vendor-audit'],
  },
  commodities: {
    intro:
      'Bulk commodities — minerals, oil, gas and other traded goods — need sampling, quantity and cargo-condition checks. Reports typically combine on-site records with partner-laboratory assay results.',
    risks: ['Non-representative sampling', 'Quantity discrepancy against declared figures', 'Cargo contamination or moisture uptake', 'Missing or incomplete chain-of-custody records'],
    items: ['Metallic and non-metallic minerals', 'Fuels and liquids in bulk (buyer-defined scope)', 'Agricultural commodities in bulk'],
    checkpoints: ['Representative sampling and chain-of-custody', 'Quantity determination — draft and ullage surveys', 'Cargo condition and contamination check', 'Laboratory assay coordination'],
    packaging: ['Bulk-bag or drum condition', 'Cargo compartment cleanliness and dryness', 'Ullage and freeboard records'],
    documentation: ['Bill of lading and packing list', 'Loading and discharge instructions', 'Any prior assay or certificate of analysis'],
    relatedServices: ['quality-inspection', 'cargo-survey', 'product-testing'],
  },
};

// Helper: get service by slug from the static services list.
export function getServiceBySlug(slug) {
  return services.find((s) => s.slug === slug);
}
